// =============================================================================
// features/sales/campaigns/detail/CampaignDetailHeader.tsx — Cabeçalho do
// painel: nome, badge de status, ação de pausar/retomar (doc 26 §6.7).
// =============================================================================

import type { Campaign } from '@elemento/shared-schemas';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { CampaignStatusBadge } from '../components';

import { PauseResumeButton } from './components/PauseResumeButton';

export interface CampaignDetailHeaderProps {
  campaign: Campaign;
  canManage: boolean;
  onPause: () => Promise<unknown>;
  onResume: () => Promise<unknown>;
  isTransitionPending: boolean;
}

export function CampaignDetailHeader({
  campaign,
  canManage,
  onPause,
  onResume,
  isTransitionPending,
}: CampaignDetailHeaderProps): React.JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => navigate('/campanhas')}
        className="self-start font-sans text-xs text-ink-3 hover:text-ink-2 transition-colors duration-fast flex items-center gap-1 min-h-[32px]"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          className="w-3.5 h-3.5"
        >
          <path d="M10 3L5 8l5 5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Campanhas
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1
              className="font-display font-bold text-ink truncate"
              style={{ fontSize: 'var(--text-2xl)', letterSpacing: '-0.03em' }}
            >
              {campaign.name}
            </h1>
            <CampaignStatusBadge status={campaign.status} />
          </div>
          {campaign.description && (
            <p className="font-sans text-sm text-ink-3 max-w-2xl">{campaign.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          {canManage && campaign.status === 'active' && (
            <a
              href={`/campanhas/${campaign.id}/operar`}
              className="inline-flex items-center min-h-[40px] px-4 rounded-sm border border-border-strong font-sans text-sm font-medium text-ink hover:bg-surface-hover transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20"
            >
              Operar
            </a>
          )}
          <PauseResumeButton
            campaignId={campaign.id}
            campaignName={campaign.name}
            status={campaign.status}
            canManage={canManage}
            onPause={onPause}
            onResume={onResume}
            isPending={isTransitionPending}
          />
        </div>
      </div>
    </div>
  );
}
